import ApiService from "./ApiService";
import { WEB_APP_CODE } from "./PayPlanUtil";

/**
 * POST /home/assistant/help — AI assistant (crossword or word search).
 * Request body encrypted in production when applicable; response may be `{ encrypted }` (decrypted by ApiService).
 * Send exactly one of gridId OR crosswordId/crossId. Always includes {@code appCode=web_app}.
 *
 * @param {{ message?: string, prompt?: string, text?: string, gridId?: string, crosswordId?: string, crossId?: string, appCode?: string }} payload
 * @returns {Promise<object>}
 */
export async function postAssistantHelp(payload) {
    const body = {
        appCode: payload?.appCode || WEB_APP_CODE,
    };
    const msg = payload?.message ?? payload?.prompt ?? payload?.text;
    if (msg != null && String(msg).trim() !== "") {
        body.message = String(msg).trim();
    }
    if (payload?.gridId) {
        body.gridId = payload.gridId;
    } else if (payload?.crosswordId) {
        body.crosswordId = payload.crosswordId;
    } else if (payload?.crossId) {
        body.crossId = payload.crossId;
    }
    return ApiService.postData("/home/assistant/help", body);
}

/**
 * GET /home/assistant/chat-history — paged chat for one puzzle (same scope rules as /help).
 * Response may be `{ encrypted }` in non-local profiles; ApiService decrypts.
 *
 * @param {{
 *   page?: number,
 *   pageSize?: number,
 *   gridId?: string,
 *   crosswordId?: string,
 *   crossId?: string,
 *   appCode?: string
 * }} opts
 * @returns {Promise<{
 *   content: Array<{ id: string, role: 'USER'|'ASSISTANT', content: string, createdAt: number }>,
 *   totalElements: number,
 *   totalPages: number,
 *   number: number,
 *   size: number
 * }>}
 */
export async function getAssistantChatHistory(opts = {}) {
    const page = opts.page ?? 0;
    const pageSize = opts.pageSize ?? 50;
    const params = {
        page,
        pageSize,
        appCode: opts.appCode || WEB_APP_CODE,
    };
    if (opts.gridId) {
        params.gridId = opts.gridId;
    } else if (opts.crosswordId) {
        params.crosswordId = opts.crosswordId;
    } else if (opts.crossId) {
        params.crossId = opts.crossId;
    } else {
        throw new Error("Exactly one of gridId or crosswordId is required.");
    }
    const data = await ApiService.getData("/home/assistant/chat-history", params);
    if (data && data.code === "FAILURE") {
        const err = new Error(data.message || "Chat history request failed");
        err.code = "FAILURE";
        err.response = data;
        throw err;
    }
    return data;
}
